import moment from "moment";
import { get, post, objToFormData, handleError } from "./utils";
import store from "@/store";
import { trackPurchase } from "./tracking";

const ROOT = process.env.VUE_APP_API;

// Contact-style forms POST to the shared endpoint on the main site (a
// Cloudflare Pages Function), which emails the submission to
// support@nanome.ai via Resend. CORS on the endpoint allows *.nanome.ai.
// `type` selects the email template; `source_url` records the page the
// form was submitted from.
const CONTACT_ENDPOINT = "https://nanome.ai/api/contact";
const submitContactForm = (type, data) => {
  return fetch(CONTACT_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      type,
      source_url: window.location.href,
      ...data
    })
  });
};

const ROUTES = {
  ADMIN: ROOT + "/admin",
  USER: ROOT + "/user",
  BILLING: ROOT + "/billing",
  CHAT: ROOT + "/chat",
  CONTACT: ROOT + "/contact",
  LICENSE: ROOT + "/licensing",
  LOG: ROOT + "/app-log",
  MEETING: ROOT + "/meeting",
  NOTIFICATION: ROOT + "/notification",
  OAUTH: ROOT + "/oauth",
  ORG: ROOT + "/organization",
  QUICKDROP: ROOT + "/quickdrop",
  SSO: ROOT + "/sso",
  VERSIONS: ROOT + "/versions"
};

export default {
  ...ROUTES,

  // User
  register(data) {
    return post(`${ROUTES.USER}/signup`, data);
  },
  verify(token) {
    return post(`${ROUTES.USER}/verify`, { token });
  },
  resend(email) {
    return post(`${ROUTES.USER}/confirm`, { email });
  },
  login({ login, pass }) {
    return post(`${ROUTES.USER}/login`, { login, pass });
  },
  loginOAuth(provider, redirect) {
    return get(`${ROUTES.OAUTH}/${provider}/login`, { redirect });
  },
  loginSSO(email, redirect) {
    return get(`${ROUTES.SSO}/login`, { email, redirect });
  },
  easyAuth(code) {
    return post(`${ROUTES.USER}/easy-auth`, { code });
  },
  getSession() {
    const storeToken = store.state.user.token.value;
    const cookieToken = document.cookie.includes("nanotoken=");
    if (!storeToken && !cookieToken) return {};

    return get(`${ROUTES.USER}/session`);
  },
  logout() {
    const token = store.state.user.token.value;
    return post(`${ROUTES.USER}/logout`, { token });
  },
  logoutOthers() {
    const token = store.state.user.token.value;
    return post(`${ROUTES.USER}/logout-others`, { token });
  },
  forgotPassword(login) {
    return post(`${ROUTES.USER}/forgot`, { login });
  },
  resetPassword({ token, pass }) {
    return post(`${ROUTES.USER}/reset`, { token, pass });
  },
  editUser({ pass, data }) {
    return post(`${ROUTES.USER}/edit`, { pass, data });
  },
  editUserMeta(data) {
    return post(`${ROUTES.USER}/meta`, { data });
  },
  editUserSettings({ email_chat, email_newsletter }) {
    const data = { email_chat, email_newsletter };
    return post(`${ROUTES.USER}/settings`, data);
  },
  updateParentalControls(enable, code) {
    return post(`${ROUTES.USER}/parental-controls`, { enable, code });
  },
  startTrial() {
    return post(`${ROUTES.USER}/start-trial`);
  },

  // Chat
  addChatAttachment(group_id, filename) {
    return post(`${ROUTES.CHAT}/add-attachment`, { group_id, filename });
  },
  createChatGroup(user_ids, name) {
    return post(`${ROUTES.CHAT}/create-group`, { user_ids, name });
  },
  deleteChatAttachment(attachment_id) {
    return post(`${ROUTES.CHAT}/delete-attachment`, { attachment_id });
  },
  deleteChatMessage(message_id) {
    return post(`${ROUTES.CHAT}/delete-message`, { message_id });
  },
  getChatAttachmentConfig() {
    return get(`${ROUTES.CHAT}/attachment-config`);
  },
  getChatAttachmentUrl(attachment_id) {
    return get(`${ROUTES.CHAT}/attachment-url`, { attachment_id });
  },
  listChatGroups() {
    return get(`${ROUTES.CHAT}/list-groups`);
  },
  listChatMessages(group_id, page = 0, count = 50) {
    return get(`${ROUTES.CHAT}/list-messages`, { group_id, page, count });
  },
  numChatUnread() {
    return get(`${ROUTES.CHAT}/num-unread`);
  },
  sendChatMessage(group_id, message, attachment_id) {
    return post(`${ROUTES.CHAT}/send-message`, {
      group_id,
      message,
      attachment_id
    });
  },
  updateChatGroupName(group_id, name) {
    return post(`${ROUTES.CHAT}/update-group-name`, { group_id, name });
  },
  updateChatLastRead(group_id) {
    return post(`${ROUTES.CHAT}/update-last-read`, { group_id });
  },

  // Contacts
  addContact(code) {
    return post(`${ROUTES.CONTACT}/add`, { code });
  },
  deleteContact(contact_id) {
    return post(`${ROUTES.CONTACT}/delete`, { contact_id });
  },
  editContact(contact_id, nickname) {
    return post(`${ROUTES.CONTACT}/edit`, { contact_id, nickname });
  },
  getContacts() {
    return get(`${ROUTES.CONTACT}/list`);
  },
  getPendingContacts() {
    return get(`${ROUTES.CONTACT}/pending`);
  },
  respondToContactRequest(contact_id, accept) {
    return post(`${ROUTES.CONTACT}/respond`, { contact_id, accept });
  },

  // 2FA
  enable2FA() {
    return post(`${ROUTES.USER}/2fa-enable`);
  },
  confirm2FA(code) {
    return post(`${ROUTES.USER}/2fa-confirm`, { code });
  },
  disable2FA() {
    return post(`${ROUTES.USER}/2fa-disable`);
  },
  generateRecoveryCodes() {
    return post(`${ROUTES.USER}/2fa-new-codes`);
  },

  // Logs
  getAppLogs() {
    return get(`${ROUTES.LOG}/list`);
  },

  // Meetings
  addMeeting(meeting) {
    return post(`${ROUTES.MEETING}/add`, meeting);
  },
  deleteMeeting(id) {
    return post(`${ROUTES.MEETING}/delete`, { id });
  },
  getMeetings() {
    return get(`${ROUTES.MEETING}/list`);
  },

  // Notifications
  deleteNotification(id) {
    return post(`${ROUTES.NOTIFICATION}/delete`, { id });
  },
  getNotifications() {
    return get(`${ROUTES.NOTIFICATION}/list`);
  },
  readNotification(id) {
    return post(`${ROUTES.NOTIFICATION}/read`, { id });
  },

  // Quick Drop
  addQuickdrop(filenames) {
    return post(`${ROUTES.QUICKDROP}/add`, { filenames });
  },
  deleteQuickdrop(filename) {
    return post(`${ROUTES.QUICKDROP}/delete`, { filename });
  },
  getQuickdrops() {
    return get(`${ROUTES.QUICKDROP}/list`);
  },
  resetQuickdrop() {
    return post(`${ROUTES.QUICKDROP}/reset`);
  },
  updateQuickdrop() {
    return post(`${ROUTES.QUICKDROP}/update`);
  },

  // Licensing
  getLicenseCapabilityTypes() {
    return get(`${ROUTES.LICENSE}/capability-types`);
  },
  licensesUsing() {
    return get(`${ROUTES.LICENSE}/using`);
  },
  licensesPending() {
    return get(`${ROUTES.LICENSE}/pending`);
  },
  licensesOwned() {
    return get(`${ROUTES.LICENSE}/owned`);
  },
  licenseAssign({ license, login, reset = false }) {
    // Pass null for login to unassign
    return post(`${ROUTES.LICENSE}/assign`, { license, login, reset });
  },
  resendInvite(license) {
    return post(`${ROUTES.LICENSE}/invite`, { license });
  },
  claimLicense({ sharerToken, receiverToken }) {
    return post(`${ROUTES.LICENSE}/accept`, {
      pending: sharerToken,
      token: receiverToken
    });
  },
  downloadLicense(license) {
    return get(`${ROUTES.LICENSE}/download?license=${license}`);
  },
  licenseData({ license, data }) {
    return post(`${ROUTES.LICENSE}/data`, { license, data });
  },

  // Billing
  getProducts() {
    return get(`${ROUTES.BILLING}/plans`);
  },
  addPaymentMethod(source) {
    return post(`${ROUTES.BILLING}/methods/add`, { source });
  },
  removePaymentMethod(source) {
    return post(`${ROUTES.BILLING}/methods/remove`, { source });
  },
  selectPaymentMethod(source) {
    return post(`${ROUTES.BILLING}/methods/select`, { source });
  },
  getPaymentMethods() {
    return get(`${ROUTES.BILLING}/methods`);
  },
  lookupCoupon(coupon) {
    return get(`${ROUTES.BILLING}/coupon?coupon=${coupon}`);
  },
  async purchase({ plan, quantity, coupon, total }) {
    const res = await post(`${ROUTES.BILLING}/subscribe`, {
      plan,
      quantity,
      coupon
    });
    if (res.success) {
      trackPurchase(res.results.subscriptionId, plan, quantity, total);
    }
    return res;
  },
  getInvoices() {
    return get(`${ROUTES.BILLING}/invoices`);
  },
  getSubscriptions() {
    return get(`${ROUTES.BILLING}/subscriptions`);
  },
  getSubscription({ subscriptionId }) {
    return get(`${ROUTES.BILLING}/subscription?id=${subscriptionId}`);
  },
  cancelSubscription({ subscriptionId, data }) {
    if (process.env.NODE_ENV === "production") {
      // Keep the Make webhook: its scenario marks the subscriber as
      // CANCELLED in Drip. Do not remove without replacing that automation.
      fetch("https://hook.integromat.com/sgmpz4rdcuvlwps16vurabkv5i6wwqup", {
        method: "POST",
        body: objToFormData(data)
      });

      // Also notify support directly (replaces the old Basin contact send)
      submitContactForm("cancellation", data);
    }

    return post(`${ROUTES.BILLING}/cancel`, { id: subscriptionId });
  },
  nextInvoice() {
    return get(`${ROUTES.BILLING}/upcoming`);
  },

  // Organization
  addOrgMember(org_id, email, role_ids) {
    return post(`${ROUTES.ORG}/add-member`, { org_id, email, role_ids });
  },
  createOrg(params) {
    return post(`${ROUTES.ORG}/create`, params);
  },
  createOrgRole(org_id, name, description) {
    return post(`${ROUTES.ORG}/roles/create`, { org_id, name, description });
  },
  deleteOrg(org_id) {
    return post(`${ROUTES.ORG}/delete`, { org_id });
  },
  deleteOrgRole(org_id, role_id) {
    return post(`${ROUTES.ORG}/roles/delete`, { org_id, role_id });
  },
  disableOrgSamlSSO(org_id) {
    return post(`${ROUTES.ORG}/sso/saml/disable`, { org_id });
  },
  editOrg(org_id, name, hubspot_id) {
    return post(`${ROUTES.ORG}/edit`, { org_id, name, hubspot_id });
  },
  enableOrgSamlSSO(org_id, metadata_url) {
    return post(`${ROUTES.ORG}/sso/saml/enable`, { org_id, metadata_url });
  },
  getOrgDetails(org_id) {
    return get(`${ROUTES.ORG}/details`, { org_id });
  },
  getOrgLogins({
    org_id,
    before = "",
    reason = "",
    search = "",
    page = 0,
    count = 10
  }) {
    return get(`${ROUTES.ORG}/member-logins`, {
      org_id,
      before,
      reason,
      search,
      page,
      count
    });
  },
  getOrgRoles(org_id) {
    return get(`${ROUTES.ORG}/roles/list`, { org_id });
  },
  manageOrgInvite(org_id, accept) {
    return post(`${ROUTES.ORG}/manage-invite`, { org_id, accept });
  },
  removeOrgMember(org_id, member_id) {
    return post(`${ROUTES.ORG}/remove-member`, { org_id, member_id });
  },
  resendOrgInvite(org_id, member_id) {
    return post(`${ROUTES.ORG}/resend-invite`, { org_id, member_id });
  },
  resetOrgMemberPassword(org_id, member_id) {
    return post(`${ROUTES.ORG}/reset-member-password`, { org_id, member_id });
  },
  updateOrgConnectivity(
    org_id,
    { mara_url, workspace_url, mara_url_alt, workspace_url_alt }
  ) {
    return post(`${ROUTES.ORG}/update-connectivity`, {
      org_id,
      mara_url,
      workspace_url,
      mara_url_alt,
      workspace_url_alt
    });
  },
  updateOrgMemberRoles(org_id, member_id, role_ids) {
    const data = { org_id, member_id, role_ids };
    return post(`${ROUTES.ORG}/update-member-roles`, data);
  },
  updateOrgMemberSSOExempt(org_id, member_id, sso_exempt) {
    const data = { org_id, member_id, sso_exempt };
    return post(`${ROUTES.ORG}/update-member-sso-exempt`, data);
  },
  updateOrgSettings(
    org_id,
    {
      require_2fa,
      require_password_change,
      prevent_password_reuse,
      enable_ip_whitelist,
      ip_whitelist,
      auth_expiry_minutes
    }
  ) {
    return post(`${ROUTES.ORG}/update-settings`, {
      org_id,
      require_2fa,
      require_password_change,
      prevent_password_reuse,
      enable_ip_whitelist,
      ip_whitelist,
      auth_expiry_minutes
    });
  },

  // Admin
  adminAuditLogSearch(params, page = 1, count = 10) {
    return get(`${ROUTES.ADMIN}/audit-log`, { ...params, page, count });
  },
  adminCreateOrg(params) {
    return post(`${ROUTES.ADMIN}/create-organization`, params);
  },
  adminMaraAddCredits(email, amount) {
    return post(`${ROUTES.ADMIN}/mara/add-credits`, { email, amount });
  },
  adminMaraSetPremium(email, is_premium, expires_at) {
    return post(`${ROUTES.ADMIN}/mara/set-premium`, {
      email,
      is_premium,
      expires_at
    });
  },
  adminOrgSearch(query, page = 1, count = 10) {
    return get(`${ROUTES.ADMIN}/org-search`, { query, page, count });
  },
  adminUserSearch(query, page = 1, count = 10, licenses = false) {
    return get(`${ROUTES.ADMIN}/user-search`, { query, page, count, licenses });
  },
  borrowSession(params) {
    return post(`${ROUTES.ADMIN}/switch`, params);
  },
  createLicense(params) {
    return post(`${ROUTES.ADMIN}/create-license`, params);
  },
  deleteLicenses(ids) {
    return post(`${ROUTES.ADMIN}/delete-license`, { ids });
  },
  setLicenseExpiry({ license_id, expired_time }) {
    const data = { license_id, expired_time };
    return post(`${ROUTES.ADMIN}/set-license-expiry`, data);
  },
  setUserPassword({ user_id, password }) {
    const data = { user_id, password };
    return post(`${ROUTES.ADMIN}/set-user-password`, data);
  },
  adminDeleteUser(user_id) {
    return post(`${ROUTES.ADMIN}/delete-user`, { user_id });
  },

  // Roles & Permissions
  getRoles() {
    return get(`${ROUTES.ADMIN}/roles/list`);
  },
  getPermissions() {
    return get(`${ROUTES.ADMIN}/roles/list-permissions`);
  },
  createRole(name, description) {
    return post(`${ROUTES.ADMIN}/roles/create`, { name, description });
  },
  deleteRole(role_id) {
    return post(`${ROUTES.ADMIN}/roles/delete`, { role_id });
  },
  editRolePermissions(role_id, permissions) {
    return post(`${ROUTES.ADMIN}/roles/edit-role-permissions`, {
      role_id,
      permissions
    });
  },
  editRoleUsers(role_id, users) {
    return post(`${ROUTES.ADMIN}/roles/edit-role-users`, { role_id, users });
  },

  // Misc
  contact(data) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("Contact form is only sent in 'production' mode");
      console.log("formData:");
      Object.entries(data).forEach(console.log);
      return Promise.resolve({});
    }

    const { url, ...fields } = data;
    fields.email = fields.email || "noreply@nanome.ai";
    if (url) fields.source_url = url;

    return submitContactForm("home-contact", fields);
  },

  requestQuote(data) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("API request blocked in dev");
      console.log(data);
      return Promise.resolve({ success: true });
    }

    return submitContactForm("quote", data)
      .then(res => {
        if (!res.ok) throw new Error("There was an error with the request.");
        return res.text();
      })
      .then(() => {
        return { success: true };
      })
      .catch(error => {
        handleError(error);
        return { success: false };
      });
  },

  // versions
  async getVersions() {
    const query = new URLSearchParams({
      sort: "number:desc",
      "pagination[pageSize]": 100,
      "pagination[withCount]": false
    });
    const url = "https://cms.nanome.ai/api/nanome-versions?" + query;
    const json = await fetch(url).then(res => res.json());
    return json.data.map(v => {
      const data = v.attributes;
      data.features = data.features && data.features.split("\n");
      data.fixes = data.fixes && data.fixes.split("\n");
      data.releaseDate = moment(data.releaseDate).format("MMMM Do, YYYY");
      return data;
    });
  },

  supportRequest(data) {
    return submitContactForm("support", data);
  }
};
